import { type ReactElement } from 'react';
import { type AdmissionOffer } from 'AdmissionOffer';
import { type CohortSectionOffer } from 'CohortSectionOffer';
import { type Cohort, getIsExecEd, getSupportEmailAddress } from 'Cohorts';
import { FormProvider, useForm } from 'FrontRoyalReactHookForm';
import { selectablePaymentSituationForTuitionPlanFrequency } from 'PaymentSituation';
import { TuitionPlanFrequency } from 'TuitionPlan';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { type CurrentUser, getAdmissionOffer, getCohort } from 'Users';
import { CohortRegistrationTuitionLedger } from '../../application_status/components/CohortRegistrationTuitionLedger';
import { useCohortDates } from '../useCohortDates';
import { useRedirectHomeWithoutCohortSectionOffer } from '../useRedirectHomeWithoutCohortSectionOffer';
import { useCohortRegistrationOptions } from '../useCohortRegistrationOptions';
import { useSelfRegister } from '../useSelfRegister';
import { RegisterButtonState } from '../TuitionAndRegistration.types';
import { RegisterButton } from './RegisterButton';
import {
    REGISTRATION_ACTION_WIDTH,
    RegistrationCard,
    RegistrationDate,
    RegistrationFooterPanel,
    RegistrationGraphic,
    RegistrationHeader,
    RegistrationNarrative,
    StartDateOptions,
} from './RegistrationLayout';
import { SmallPrint } from './SmallPrint';
import { LOCALE_PREFIX } from '../constants';

type FormValues = {
    registerForCohortId: string;
};

/*
    The learner owes nothing, so there is no plan to choose and no Stripe Checkout session: the
    register button registers them directly.

    See https://trello.com/c/4pcyVnov.
*/
export function CohortRegistrationFullScholarship(): ReactElement | null {
    const currentUser = useRequiredCurrentUser();

    const cohort = getCohort(currentUser);
    const admissionOffer = getAdmissionOffer(currentUser);
    const cohortSectionOffer = admissionOffer?.cohortSectionOffers?.[0];

    useRedirectHomeWithoutCohortSectionOffer({
        cohortSectionOffer,
        currentUser,
        screenName: 'cohort_registration_full_scholarship',
    });

    if (!cohort || !admissionOffer || !cohortSectionOffer) return null;

    return (
        <FullScholarshipBody
            currentUser={currentUser}
            cohort={cohort}
            admissionOffer={admissionOffer}
            cohortSectionOffer={cohortSectionOffer}
        />
    );
}

type FullScholarshipBodyProps = {
    currentUser: CurrentUser;
    cohort: Cohort;
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
};

function FullScholarshipBody({
    currentUser,
    cohort,
    admissionOffer,
    cohortSectionOffer,
}: FullScholarshipBodyProps): ReactElement {
    const dates = useCohortDates(currentUser);
    const registrationOptions = useCohortRegistrationOptions(currentUser, dates?.classStartDate ?? '');
    const { processingRegistration, selfRegister } = useSelfRegister();

    // The plan the learner registers on. Absent only if the offer carries no full scholarship plan.
    const fullScholarshipTuitionPlanId = selectablePaymentSituationForTuitionPlanFrequency(
        cohortSectionOffer.studentSelectablePaymentSituations,
        TuitionPlanFrequency.FullScholarship,
    )?.tuitionPlan.id;

    const methods = useForm<FormValues>({
        defaultValues: {
            // Preselect the first start date, falling back to the offer cohort.
            registerForCohortId: registrationOptions.defaultCohortId ?? admissionOffer.cohortId,
        },
    });

    const isDisabled = !dates?.registerEnabled || processingRegistration;

    const handleRegister = () => {
        if (!fullScholarshipTuitionPlanId) return;
        const values = methods.getValues();

        selfRegister({
            admission_offer_id: admissionOffer.id,
            cohort_id: values.registerForCohortId,
            tuition_plan_id: fullScholarshipTuitionPlanId,
        });
    };

    return (
        <FormProvider {...methods}>
            <RegistrationCard>
                <RegistrationHeader>
                    <RegistrationGraphic
                        titleKey={`${LOCALE_PREFIX}.graphic_full_scholarship_title`}
                        subtitleKey={`${LOCALE_PREFIX}.graphic_full_scholarship_subtitle`}
                        scholarshipAmount={cohortSectionOffer.cumulativeOfferedScholarship?.amount}
                        numOfferedScholarships={0}
                    />

                    <RegistrationNarrative
                        tuitionDetailsLabelKey={`${LOCALE_PREFIX}.tuition_and_scholarship_details`}
                        narrativeKey={`${LOCALE_PREFIX}.narrative_full_scholarship`}
                        numOfferedScholarships={0}
                    />
                </RegistrationHeader>

                <CohortRegistrationTuitionLedger
                    admissionOffer={admissionOffer}
                    cohortSectionOffer={cohortSectionOffer}
                    className="-mx-5 mt-5 px-5"
                />

                {dates ? (
                    <RegistrationDate
                        dates={dates}
                        isExecEd={getIsExecEd(cohort)}
                        admissionsEmail={getSupportEmailAddress(cohort, 'admissions') ?? ''}
                        className="-mx-5"
                    />
                ) : null}

                <RegistrationFooterPanel>
                    {/*
                        Centred rather than in the right column: this screen has no plan column.
                    */}
                    <div className="flex flex-col items-center gap-5">
                        {registrationOptions.showMultipleCohortRegistrationOptions ? (
                            <StartDateOptions
                                options={registrationOptions.cohortRegistrationOptions}
                                // Only the button waits for registration to open.
                                isDisabled={processingRegistration}
                            />
                        ) : null}

                        <RegisterButton
                            processingRegistration={processingRegistration}
                            // No Stripe session is involved, so Cordova registers in place.
                            registerRequiresRedirectToBrowser={false}
                            defaultState={RegisterButtonState.register}
                            isDisabled={isDisabled}
                            onPress={handleRegister}
                            className={`w-full ${REGISTRATION_ACTION_WIDTH}`}
                        />
                    </div>
                </RegistrationFooterPanel>
            </RegistrationCard>

            <SmallPrint currentUser={currentUser} hasScholarship />
        </FormProvider>
    );
}
